import { useQuery } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { useId, useState } from 'react';
import type { AdminApi, CommentPerson } from '../../api/client.ts';

/** The word being typed after `@`, if the caret is in one. */
const mentionAt = (text: string): string | null => {
  const m = /(?:^|\s)@([^\s@]*)$/.exec(text);
  return m ? (m[1] ?? '') : null;
};

/**
 * A comment field with `@` mentions (BB-21): typing `@` and part of a name or email suggests colleagues; choosing one
 * writes `@email` into the text and remembers who was mentioned.
 */
export function MentionInput({ environmentId, label, value, mentions, onChange, disabled }: { environmentId: string; label: string; value: string; mentions: string[]; onChange: (value: string, mentions: string[]) => void; disabled?: boolean }) {
  const { api } = useRouter().options.context as { api: AdminApi };
  const id = useId();
  const [focused, setFocused] = useState(false);
  const query = mentionAt(value);
  const candidates = useQuery({ queryKey: ['comment-mentions', environmentId, query], queryFn: () => api.mentionCandidates(environmentId, query ?? ''), enabled: focused && query !== null });
  const pick = (person: CommentPerson) => {
    const next = value.replace(/@([^\s@]*)$/, `@${person.email} `);
    onChange(next, [...new Set([...mentions, person.id])]);
  };
  const options = query !== null && focused ? (candidates.data?.items ?? []) : [];
  return (
    <div className="mention-input">
      <label htmlFor={id}>{label}</label>
      <textarea
        id={id}
        value={value}
        disabled={disabled}
        maxLength={5000}
        onFocus={() => setFocused(true)}
        onBlur={() => setTimeout(() => setFocused(false), 150)}
        onChange={(e) => onChange(e.target.value, mentions)}
        aria-autocomplete="list"
        aria-controls={options.length ? `${id}-people` : undefined}
      />
      {options.length > 0 && (
        <ul id={`${id}-people`} role="listbox" aria-label="Mention someone" className="mention-input__options">
          {options.map((p) => (
            <li key={p.id} role="option" aria-selected={false} tabIndex={-1} onMouseDown={(e) => e.preventDefault()} onClick={() => pick(p)}>
              {p.name ? `${p.name} · ` : ''}
              {p.email}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
